/**
 * 掲示板（board_posts / board_categories / thread_messages）データモデル（v3.5 / 2026-07-24）。
 *
 * 社内メンバー向け掲示板。「決定確認 / 作業依頼 / 共有事項」の3タイプの投稿を
 * カテゴリー別に整理し、宛先者のアクション（決定・完了・確認済 等）をステータスとして追跡する。
 * 外部SA（level==='external'）には非表示（確定仕様。rules / callable で内部限定）。
 *
 * - board_posts の書込は Callable（upsertBoardPost / actOnBoardPost / closeBoardPost 等）のみ
 * - board_categories / thread_messages はクライアント直書き（rules の作成者バインドで保護）
 * - Phase 1 は audience='all' 固定（recipientStates 等はスキーマだけ先行定義し既定値で保存）
 *
 * 参照: docs/02_work/done/plan_board_feature.md
 */
import type { IsoOrTimestamp } from './common';

/** 投稿タイプ: 決定確認 / 作業依頼 / 共有事項。 */
export type BoardPostType = 'decision' | 'task' | 'info';

/** 緊急度: 高 / 中 / 低。 */
export type BoardUrgency = 'high' | 'medium' | 'low';

/**
 * 宛先者ごとのステータス。
 * - unread / read: 全タイプ共通（未確認 → 既読）
 * - decided / rejected / onhold: 決定確認（決定 / 却下 / 保留。保留は作業依頼と共用）
 * - done / cancelled: 作業依頼（完了 / キャンセル）
 * - acknowledged: 共有事項（確認済）
 */
export type BoardRecipientState =
  | 'unread'
  | 'read'
  | 'decided'
  | 'rejected'
  | 'onhold'
  | 'done'
  | 'cancelled'
  | 'acknowledged';

/** 宛先者 1 名分の状態スナップショット（BoardPost.recipientStates の値）。 */
export interface BoardRecipientStateEntry {
  state: BoardRecipientState;
  readAt: IsoOrTimestamp | null;
  actedAt: IsoOrTimestamp | null;
}

/**
 * 掲示板投稿（board_posts/{postId}）。
 *
 * openRecipientUids / isOpen は導出フィールド（書込 Callable が
 * recomputeBoardPostDerived で再計算する不変条件。plan §2-2-1）:
 * - openRecipientUids = (closedAt != null || audience !== 'selected')
 *                       ? [] : recipientUids のうち state が unread/read/onhold のもの
 * - isOpen            = openRecipientUids.length > 0
 */
export interface BoardPost {
  type: BoardPostType;
  title: string;
  /** TipTap HTML（既存 RichTextField と同じ HTML 文字列運用）。 */
  bodyHtml: string;
  /** タグ除去済みプレーンテキスト（AI 入力・一覧フォールバック用、保存時に生成）。 */
  bodyText: string;
  /** null = 未分類。 */
  categoryId: string | null;
  urgency: BoardUrgency;
  /** 'YYYY-MM-DD' (JST)。null = 無期日。 */
  dueDate: string | null;
  /** Phase 1 は 'all' 固定。 */
  audience: 'all' | 'selected';
  /** selected 時のみ。all 時は []。 */
  recipientUids: string[];
  /** selected 時のみ。key = 宛先 uid。 */
  recipientStates: Record<string, BoardRecipientStateEntry>;
  /** 導出: 「未対応」(unread/read/onhold) の宛先 uid。 */
  openRecipientUids: string[];
  /** 導出: openRecipientUids.length > 0。 */
  isOpen: boolean;
  authorUid: string;
  /** 表示名スナップショット（admins read 制限 + 退職者 orphan 対策）。 */
  authorName: string;
  /** 宛先のアクション後 true / 投稿者が詳細を開くと false（投稿者バッジ用）。 */
  authorUnseenActivity: boolean;
  /** AI 要約（最大80字）。未生成時は bodyText 先頭でフォールバック表示。 */
  aiSummary: string | null;
  /** bodyText+title のハッシュ。再要約判定 & 無限ループ防止ガード。 */
  aiSummarySourceHash: string | null;
  /** スレッド件数（onThreadMessageWritten トリガーが維持）。 */
  threadCount: number;
  /**
   * uid → その人が最後にスレッドを開いた時点の threadCount。
   * 一覧のスレッド新着バッジ = threadCount - (threadSeen[me] ?? 0)。
   */
  threadSeen: Record<string, number>;
  closedAt: IsoOrTimestamp | null;
  closedBy: string | null;
  /** 期日リマインダー送信段階（0=未送 1=超過時送信済 2=1週間後送信済）。 */
  reminderStage: 0 | 1 | 2;
  /**
   * 投稿予約（2026-07-30 追加）: 実際に公開される日時。
   * null = 予約なし（保存と同時に公開。従来どおりの挙動）。
   * 予約が過ぎて公開された後も履歴として残る（publishedAt が入る）。
   * ★ 既存 doc には存在しないため optional。undefined = 予約なし。
   */
  publishAt?: IsoOrTimestamp | null;
  /**
   * 公開された日時。null = 予約中（未公開）。
   * ★ 既存 doc には存在しないため optional。undefined = 公開済み（後方互換）。
   *   「未公開」の判定は必ず isBoardPostPendingPublish() を通すこと。
   */
  publishedAt?: IsoOrTimestamp | null;
  /**
   * 閲覧制限（2026-09-07 追加）: true のとき、宛先に指定された人と投稿者本人だけが
   * 一覧・詳細で閲覧できる。宛先が「全員」(audience='all') の場合は制限の対象者が
   * いないため、ON で保存要求されても upsertBoardPost が false に落として保存する。
   * ★ 既存 doc には存在しないため optional。undefined = 制限なし。
   *   閲覧可否の判定は必ず canViewBoardPost() を通すこと。
   */
  restricted?: boolean;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/**
 * 閲覧制限が実際に効いている（宛先以外には見せない）投稿か。
 * restricted=true かつ宛先指定あり (audience='selected') のときだけ true。
 */
export function isBoardPostRestricted(post: {
  restricted?: boolean;
  audience: 'all' | 'selected';
}): boolean {
  return post.restricted === true && post.audience === 'selected';
}

/**
 * 指定ユーザーがその投稿を閲覧できるか（一覧・詳細の表示判定）。
 *
 * 非表示になるのは次のどちらか:
 * - 予約中（未公開）で、投稿者本人ではない
 * - 閲覧制限あり（isBoardPostRestricted）で、投稿者本人でも宛先でもない
 *
 * 通知は宛先指定の投稿なら元々宛先にしか届かないため、ここでは表示だけを扱う。
 */
export function canViewBoardPost(
  post: {
    authorUid: string;
    audience: 'all' | 'selected';
    recipientUids: string[];
    restricted?: boolean;
    publishAt?: IsoOrTimestamp | null;
    publishedAt?: IsoOrTimestamp | null;
  },
  uid: string | null,
): boolean {
  const isAuthor = uid != null && post.authorUid === uid;
  if (isBoardPostPendingPublish(post) && !isAuthor) return false;
  if (isBoardPostRestricted(post)) {
    const isRecipient = uid != null && (post.recipientUids ?? []).includes(uid);
    if (!isAuthor && !isRecipient) return false;
  }
  return true;
}

/**
 * 「予約中（まだ公開されていない）」判定。
 *
 * ★ 単に `publishedAt == null` で判定してはいけない: 機能追加前に作成された
 *   既存 doc は publishedAt を持たない（undefined）ため、全件が未公開に化ける。
 *   publishAt が入っていることを条件に含めることで後方互換を保つ。
 *
 * 予約中の投稿は:
 * - 一覧・詳細で投稿者本人以外に見えない
 * - 通知を送らない（公開時にまとめて送る）
 * - 導出フィールド（openRecipientUids / isOpen）が空 = バッジ・期日リマインダーの対象外
 */
export function isBoardPostPendingPublish(post: {
  publishAt?: IsoOrTimestamp | null;
  publishedAt?: IsoOrTimestamp | null;
}): boolean {
  return post.publishAt != null && post.publishedAt == null;
}

/** 掲示板カテゴリー（board_categories/{categoryId}）。 */
export interface BoardCategory {
  name: string;
  /** 一覧の表示順（手動並べ替えは将来、当面は作成順）。 */
  order: number;
  /** カテゴリー作成者の admin uid。 */
  createdBy: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/**
 * 投稿テンプレート（board_templates/{templateId}）。
 * 件名 + 本文（+ 推奨タイプ / 緊急度）のプリセット。新規投稿画面の
 * 「テンプレートから選択」モーダルで適用・追加する。
 * 標準テンプレートはコード定数（admin-web/boardTemplates.ts）、
 * ここに保存されるのはユーザーが追加したカスタムテンプレートのみ。
 */
export interface BoardTemplate {
  /** テンプレート名（活用シーン）。 */
  name: string;
  /** 適用時に設定する投稿タイプ（省略時は現在の選択を維持）。 */
  type?: BoardPostType;
  /** 適用時に設定する緊急度（省略時は現在の選択を維持）。 */
  urgency?: BoardUrgency;
  /** 件名テンプレート。 */
  subject: string;
  /** 本文テンプレート（TipTap 互換 HTML）。 */
  bodyHtml: string;
  /** 作成者の admin uid。 */
  createdBy: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/** スレッドメッセージ（board_posts/{postId}/thread_messages/{messageId}）。 */
export interface BoardThreadMessage {
  authorUid: string;
  /** 表示名スナップショット。 */
  authorName: string;
  /** プレーンテキスト（表示時に URL 自動リンク化）。 */
  body: string;
  /** Storage ダウンロード URL（0〜複数）。 */
  imageUrls: string[];
  /** 絵文字リアクション。配列順 = 時系列（末尾が最新）。 */
  reactions: Array<{ uid: string; emoji: string }>;
  createdAt: IsoOrTimestamp;
  editedAt: IsoOrTimestamp | null;
}

// ─────────────────────────────────────────────
// ラベル定数
// ─────────────────────────────────────────────

export const BOARD_POST_TYPE_LABEL: Record<BoardPostType, string> = {
  decision: '決定確認',
  task: '作業依頼',
  info: '共有事項',
};

export const BOARD_URGENCY_LABEL: Record<BoardUrgency, string> = {
  high: '高',
  medium: '中',
  low: '低',
};

export const BOARD_RECIPIENT_STATE_LABEL: Record<BoardRecipientState, string> = {
  unread: '未確認',
  read: '既読',
  decided: '決定',
  rejected: '却下',
  onhold: '保留',
  done: '完了',
  cancelled: 'キャンセル',
  acknowledged: '確認済',
};

/** 終端状態（対応完了の判定に使う。保留 onhold は「未対応」として残る）。 */
export const BOARD_TERMINAL_STATES: readonly BoardRecipientState[] = [
  'decided',
  'rejected',
  'done',
  'cancelled',
  'acknowledged',
] as const;

/** タイプ別に宛先者が選択できるアクション（actOnBoardPost がサーバ側でも検証）。 */
export const BOARD_TYPE_ACTIONS: Record<BoardPostType, readonly BoardRecipientState[]> = {
  decision: ['decided', 'rejected', 'onhold'],
  task: ['done', 'onhold', 'cancelled'],
  info: ['acknowledged'],
};

/**
 * スレッドメッセージのリアクションで選択できる絵文字（Phase 3）。
 * リアクション追加ポップオーバーの「+」から選ぶ候補一覧。配列順が表示順。
 */
export const BOARD_REACTION_EMOJIS = ['👍', '🙏', '✅', '👀', '🎉', '😂', '❤️', '💡'] as const;

/** コレクション名（snake_case 複数形の既存慣例）。 */
export const BOARD_COLLECTIONS = {
  posts: 'board_posts',
  categories: 'board_categories',
  threadMessages: 'thread_messages',
  templates: 'board_templates',
  attachments: 'attachments',
  actionReports: 'action_reports',
} as const;

// ─────────────────────────────────────────────
// 添付ファイル（成果物）— 2026-07-27 追加
// ─────────────────────────────────────────────

// ─────────────────────────────────────────────
// 対応内容（アクション時のレポート）— 2026-07-28 追加
// ─────────────────────────────────────────────

/**
 * 宛先者がアクション（完了/保留/キャンセル）時に添える対応内容。
 * `board_posts/{postId}/action_reports/{uid}` — 宛先者 1 名につき 1 件（上書き）。
 *
 * ★ recipientStates に持たせない理由は添付と同じ:
 *   actOnBoardPost がエントリを毎回作り直すため、そこに置くと消える。
 */
export interface BoardActionReport {
  /** 参考 URL（http/https のみ）。未入力は null。 */
  url: string | null;
  /** リッチテキスト（TipTap HTML）。サーバで sanitizeBoardHtml 済み。 */
  bodyHtml: string;
  /** 記入者の表示名スナップショット。 */
  uploaderName: string;
  /** 記入時のアクション。 */
  state: BoardRecipientState;
  updatedAt: IsoOrTimestamp;
}

/** クライアント表示用（uid を id として持つ）。 */
export interface BoardActionReportView extends Omit<BoardActionReport, 'updatedAt'> {
  uid: string;
  updatedAt: string | null;
}

/** 添付の保護方式。password の場合のみダウンロード時にパスワード照合を行う。 */
export type BoardAttachmentProtection = 'none' | 'password';

/**
 * 添付の種別。
 * - 'reference'   : 投稿者が付ける参考資料（編集画面。作業時に参照してほしい資料）
 * - 'deliverable' : 宛先者が付ける成果物（詳細画面。アクション時に提出）
 */
export type BoardAttachmentKind = 'reference' | 'deliverable';

/** 添付ファイルの上限サイズ（100MB。board/videos と同値）。 */
export const BOARD_ATTACHMENT_MAX_BYTES = 100 * 1024 * 1024;

/**
 * 宛先者がアクション時に添付する成果物ファイル。
 * `board_posts/{postId}/attachments/{attachmentId}` サブコレクション。
 *
 * ★ recipientStates に持たせない理由: actOnBoardPost がエントリを毎回作り直すため、
 *   そこに置くとアクションのたびに添付が消える。
 * ★ storagePath を保存し downloadURL は保存しない。ダウンロードは常に
 *   resolveBoardAttachmentDownload が発行する短命の署名付き URL 経由。
 */
export interface BoardAttachment {
  /** 種別（既定は 'deliverable'。後方互換のため任意）。 */
  kind?: BoardAttachmentKind;
  /** アップロードした宛先者の uid。 */
  uploaderUid: string;
  /** 表示名スナップショット（退職者 orphan 対策）。 */
  uploaderName: string;
  /** 添付時のアクション（done / onhold / cancelled）。reference では未使用。 */
  state?: BoardRecipientState;
  /** 元のファイル名（表示・ダウンロード時のファイル名）。 */
  fileName: string;
  /** バイト数。 */
  size: number;
  /** MIME（ブラウザ申告値。未取得なら空文字）。 */
  contentType: string;
  /** Storage のオブジェクトパス（board/files/{postId}/...）。 */
  storagePath: string;
  /** 'password' のときのみ passwordHash を検証する。 */
  protection: BoardAttachmentProtection;
  /**
   * scrypt ハッシュ。平文は保存しない（忘れた場合は再設定）。
   * クライアントには絶対に返さない（Firestore ルールで read を塞ぐのではなく、
   * 一覧取得を Callable 経由にして射影する）。
   */
  passwordHash?: string;
  createdAt: IsoOrTimestamp;
  updatedAt: IsoOrTimestamp;
}

/** クライアントへ返す添付の公開ビュー（passwordHash を含まない）。 */
export interface BoardAttachmentView {
  id: string;
  kind: BoardAttachmentKind;
  uploaderUid: string;
  uploaderName: string;
  state: BoardRecipientState | null;
  fileName: string;
  size: number;
  contentType: string;
  protection: BoardAttachmentProtection;
  createdAt: string | null;
}
